/**
 * Solo subscription view — business logic hook
 *
 * Owns the local "pending change" state (tier + add-ons the user has clicked
 * but not yet confirmed), derives the current state from the org's actual
 * subscription, and orchestrates the commit (sequential per-bundle mutations).
 * Matches the EditPlansModal pattern: tier swap via changeMember, add-ons via
 * add/remove, all targeting the current user (the sole member of a solo org).
 */

import { useCallback, useMemo, useState } from 'react';
import {
  useAddMemberBundleMutation,
  useChangeMemberBundleMutation,
  useRemoveMemberBundleMutation,
} from '@features/organizations';
import { useUserId } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type { FullSubscriptionDetailsResponse } from '@store/generated/subscriptions';
import { useSnackbar } from 'notistack';

import { useGetUserBundlesQuery, useUpdateBillingIntervalMutation } from '../../store/api';
import type { BillingInterval, BundleWithStatus } from '../../types';
import { getAddOnsIncludedInTier, getMonthlyAmountCents } from '../../utils';

interface UseSoloSubscriptionArgs {
  subscription: FullSubscriptionDetailsResponse;
  tiers: BundleWithStatus[];
  addOns: BundleWithStatus[];
  onCommitted: () => void;
}

const setsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every(id => b.has(id));

export function useSoloSubscription({
  subscription,
  tiers,
  addOns,
  onCommitted,
}: UseSoloSubscriptionArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const { subscriptionManagement } = useEventTracking();
  const userId = useUserId();
  const currentBillingInterval: BillingInterval =
    subscription.billing_interval === 'year' ? 'year' : 'month';

  // User-level bundles give us the assignment_ids needed for change/remove
  // mutations (line_items only carry stripe_subscription_item_id, not the
  // per-assignment id). ConfirmPaymentStep uses the same source.
  const { data: userBundlesData } = useGetUserBundlesQuery();
  const userBundles = useMemo(
    () => userBundlesData?.bundles ?? [],
    [userBundlesData?.bundles],
  );

  // Current state derived from the user's actual bundles.
  const currentTierAssignment = useMemo(
    () => userBundles.find(b => b.bundle_type === 'tier'),
    [userBundles],
  );
  const currentTierId = currentTierAssignment?.bundle_id || '';
  const currentAddOnIds = useMemo(
    () => new Set(userBundles.filter(b => b.bundle_type === 'add_on').map(b => b.bundle_id)),
    [userBundles],
  );

  // Local pending selection. `null` = no change made yet (derived state mirrors
  // the server); concrete values override.
  const [selectedTierId, setSelectedTierIdState] = useState<string | null>(null);
  const [selectedAddOnIds, setSelectedAddOnIdsState] = useState<Set<string> | null>(null);
  const [previewInterval, setPreviewInterval] = useState<BillingInterval | null>(null);

  const effectiveTierId = selectedTierId ?? currentTierId;
  const effectiveAddOnIds = selectedAddOnIds ?? currentAddOnIds;
  const effectiveInterval: BillingInterval = previewInterval ?? currentBillingInterval;
  const isAnnualBilling = effectiveInterval === 'year';
  const hasIntervalChange = previewInterval !== null && previewInterval !== currentBillingInterval;

  // Surface a normalized setter so the view can pass it to BillingIntervalToggle
  // without leaking the "null clears preview" detail. Picking the current
  // interval clears the preview so the sidebar drops the pending state.
  const setBillingInterval = useCallback(
    (interval: BillingInterval) => {
      setPreviewInterval(interval === currentBillingInterval ? null : interval);
    },
    [currentBillingInterval],
  );

  // Add-ons fully covered by the effective tier's grants — same grants-driven
  // lock + double-bill guard used by Person Card / Edit Plans modals.
  const tierCoveredAddOnIds = useMemo(() => {
    const tier = tiers.find(t => t.bundle_id === effectiveTierId);
    if (!tier) return new Set<string>();
    return new Set(getAddOnsIncludedInTier(tier, addOns).map(a => a.bundle_id));
  }, [tiers, addOns, effectiveTierId]);

  const setTier = useCallback(
    (newTierId: string) => {
      setSelectedTierIdState(newTierId);
      // Drop any selected add-ons that the new tier already covers — keeps the
      // wire payload clean + matches the locked rows the UI is about to show.
      const tier = tiers.find(t => t.bundle_id === newTierId);
      if (!tier) return;
      const covered = new Set(getAddOnsIncludedInTier(tier, addOns).map(a => a.bundle_id));
      const base = selectedAddOnIds ?? currentAddOnIds;
      const filtered = new Set([...base].filter(id => !covered.has(id)));
      if (filtered.size !== base.size) {
        setSelectedAddOnIdsState(filtered);
      } else if (selectedAddOnIds === null) {
        // First selection; lock in the current set so later toggles diff
        // against an immutable baseline rather than re-pulling currentAddOnIds.
        setSelectedAddOnIdsState(new Set(base));
      }
    },
    [tiers, addOns, selectedAddOnIds, currentAddOnIds],
  );

  const toggleAddOn = useCallback(
    (bundleId: string) => {
      const base = selectedAddOnIds ?? currentAddOnIds;
      const next = new Set(base);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      setSelectedAddOnIdsState(next);
    },
    [selectedAddOnIds, currentAddOnIds],
  );

  // Cost math — tier monthly + paid add-ons (those NOT covered by tier grants).
  const computeCost = useCallback(
    (tierId: string, addOnIds: Set<string>): { cents: number; currency: string } => {
      const tier = tiers.find(t => t.bundle_id === tierId);
      if (!tier) return { cents: 0, currency: 'usd' };
      const memberAddOns: BundleWithStatus[] = Array.from(addOnIds)
        .map(id => addOns.find(a => a.bundle_id === id))
        .filter((b): b is BundleWithStatus => b != null);
      const included = new Set(
        getAddOnsIncludedInTier(tier, memberAddOns).map(a => a.bundle_id),
      );
      const paid = memberAddOns.filter(a => !included.has(a.bundle_id));
      const tierCents = getMonthlyAmountCents(tier, isAnnualBilling);
      const addOnsCents = paid.reduce(
        (sum, a) => sum + getMonthlyAmountCents(a, isAnnualBilling),
        0,
      );
      return { cents: tierCents + addOnsCents, currency: tier.currency };
    },
    [tiers, addOns, isAnnualBilling],
  );

  const currentCost = useMemo(
    () => computeCost(currentTierId, currentAddOnIds),
    [computeCost, currentTierId, currentAddOnIds],
  );
  const projectedCost = useMemo(
    () => computeCost(effectiveTierId, effectiveAddOnIds),
    [computeCost, effectiveTierId, effectiveAddOnIds],
  );

  const hasChanges = useMemo(() => {
    if (effectiveTierId !== currentTierId) return true;
    if (hasIntervalChange) return true;
    return !setsEqual(effectiveAddOnIds, currentAddOnIds);
  }, [
    effectiveTierId,
    currentTierId,
    effectiveAddOnIds,
    currentAddOnIds,
    hasIntervalChange,
  ]);

  const revert = useCallback(() => {
    setSelectedTierIdState(null);
    setSelectedAddOnIdsState(null);
    setPreviewInterval(null);
  }, []);

  // Commit
  const [changeMemberBundle, { isLoading: isChangingBundle }] = useChangeMemberBundleMutation();
  const [addMemberBundle, { isLoading: isAddingBundle }] = useAddMemberBundleMutation();
  const [removeMemberBundle, { isLoading: isRemovingBundle }] = useRemoveMemberBundleMutation();
  const [updateBillingInterval, { isLoading: isUpdatingInterval }] =
    useUpdateBillingIntervalMutation();
  const isCommitting =
    isChangingBundle || isAddingBundle || isRemovingBundle || isUpdatingInterval;

  const commit = useCallback(async () => {
    if (!hasChanges || !userId) return;

    try {
      // Tier swap first — atomic via changeMemberBundle when a current
      // assignment exists, fall through to add when there isn't one.
      if (effectiveTierId !== currentTierId) {
        if (currentTierAssignment && currentTierId && effectiveTierId) {
          await changeMemberBundle({
            userId,
            assignmentId: currentTierAssignment.assignment_id,
            adminAddBundleRequest: { bundle_id: effectiveTierId },
          }).unwrap();
        } else if (effectiveTierId) {
          await addMemberBundle({
            userId,
            adminAddBundleRequest: { bundle_id: effectiveTierId },
          }).unwrap();
        }
      }

      const addOnsToAdd = [...effectiveAddOnIds].filter(
        id => !currentAddOnIds.has(id) && !tierCoveredAddOnIds.has(id),
      );
      const addOnsToRemove = [...currentAddOnIds].filter(id => !effectiveAddOnIds.has(id));

      for (const bundleId of addOnsToAdd) {
        await addMemberBundle({
          userId,
          adminAddBundleRequest: { bundle_id: bundleId },
        }).unwrap();
      }
      for (const bundleId of addOnsToRemove) {
        const assignment = userBundles.find(
          b => b.bundle_id === bundleId && b.bundle_type === 'add_on',
        );
        if (assignment) {
          await removeMemberBundle({
            userId,
            assignmentId: assignment.assignment_id,
          }).unwrap();
        }
      }

      // Billing interval swap last — bundle mutations have already settled,
      // so changing the cycle won't catch a half-migrated subscription. Fires
      // its own analytics event so the funnel matches the group-side flow.
      if (hasIntervalChange && previewInterval) {
        await updateBillingInterval({
          changeBillingIntervalRequest: { billing_interval: previewInterval },
        }).unwrap();
        subscriptionManagement.billingIntervalConfirmed({
          from_interval: currentBillingInterval,
          to_interval: previewInterval,
        });
      }

      // Outcome event for the committed plan change — the solo analog of the
      // group-side `practice.batchEditApplied`. (Not `tierComparisonViewed`,
      // which is a view event fired on the group overview's mount.)
      subscriptionManagement.soloPlanChanged({
        subscription_status: subscription.subscription_status,
        billing_interval: subscription.billing_interval,
        is_trialing: subscription.is_trialing,
        tier_count: tiers.length,
        addon_count: addOns.length,
      });

      enqueueSnackbar('Subscription updated successfully', { variant: 'success' });
      revert(); // clear local state so the next render reflects the refetched server state
      onCommitted();
    } catch (error) {
      const message =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
          ? String(error.data.message)
          : 'Failed to update subscription';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [
    hasChanges,
    userId,
    effectiveTierId,
    currentTierId,
    currentTierAssignment,
    effectiveAddOnIds,
    currentAddOnIds,
    tierCoveredAddOnIds,
    userBundles,
    tiers.length,
    addOns.length,
    subscription.subscription_status,
    subscription.billing_interval,
    subscription.is_trialing,
    hasIntervalChange,
    previewInterval,
    currentBillingInterval,
    changeMemberBundle,
    addMemberBundle,
    removeMemberBundle,
    updateBillingInterval,
    enqueueSnackbar,
    revert,
    onCommitted,
    subscriptionManagement,
  ]);

  return {
    // selection state
    currentTierId,
    currentAddOnIds,
    effectiveTierId,
    effectiveAddOnIds,
    tierCoveredAddOnIds,
    effectiveInterval,
    currentBillingInterval,
    // change detection + costs
    hasChanges,
    currentCost,
    projectedCost,
    // handlers
    setTier,
    toggleAddOn,
    setBillingInterval,
    revert,
    commit,
    isCommitting,
  };
}
